import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Clock, ArrowRight, User, Briefcase, PenLine, Shield, Trophy, Zap } from "lucide-react";
import DashboardShell from "../components/layout/DashboardShell";
import { Card } from "../components/common/Primitives";
import { useAuth } from "../lib/AuthContext";
import api, { formatApiError } from "../lib/api";

const STAGE_ICONS = {
  nda: Shield,
  loi: PenLine,
  term_sheet: PenLine,
  definitive: PenLine,
  closed: CheckCircle2,
};

const STATUS_LABEL = {
  signed: "Signed",
  awaiting_signatures: "Awaiting sign",
  drafted: "Drafted",
  not_started: "—",
  closed: "Closed",
};

/** Compact owner-cue chip. Green when action is on the OTHER party, amber
 *  when it's on YOU (so you can jump in immediately), grey when both parties
 *  have already delivered (waiting on the signer platform). */
function OwnerCue({ owner, hint, viewerRole }) {
  if (owner === "none") return null;
  const isMine =
    (viewerRole === "investor" && owner === "investor") ||
    (viewerRole === "startup" && owner === "startup") ||
    owner === "both";
  return (
    <div
      className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-md mt-1 inline-flex items-center gap-1 ${
        isMine
          ? "bg-amber-500/10 text-amber-800 border border-amber-500/40"
          : "bg-emerald-500/10 text-emerald-800 border border-emerald-500/40"
      }`}
    >
      <User size={10} /> {isMine ? "Your move" : `${owner} to act`}
      <span className="hidden sm:inline text-ink-muted normal-case tracking-normal">· {hint}</span>
    </div>
  );
}

export default function DealVelocityBoard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/deal-rooms/velocity")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    api.get("/deal-rooms/velocity/insights")
      .then((r) => setInsights(r.data))
      .catch(() => {});
    api.get("/deal-rooms/velocity/leaderboard?limit=8")
      .then((r) => setLeaderboard(r.data))
      .catch(() => {});
  }, []);

  const stages = data?.stages || [];
  const cards = data?.cards || [];
  const totals = data?.totals || {};

  // Cards grouped by their CURRENT (uncompleted) stage — this is how the
  // Kanban lanes are populated.
  const byStage = useMemo(() => {
    const m = {};
    for (const s of stages) m[s.key] = [];
    for (const c of cards) (m[c.current_stage] ||= []).push(c);
    return m;
  }, [stages, cards]);

  return (
    <DashboardShell
      title="Deal Velocity"
      subtitle="Where every deal room is right now — NDA to Definitive to Signed. Amber chips are on your bench."
    >
      <InsightsRibbon insights={insights} viewerRole={user?.role} />
      <SectorLeaderboard leaderboard={leaderboard} />
      {loading ? (
        <Card><div className="text-sm text-ink-muted">Loading…</div></Card>
      ) : cards.length === 0 ? (
        <Card>
          <div className="text-center py-10">
            <Briefcase size={22} className="text-ink-muted mx-auto mb-2 opacity-50" />
            <div className="font-heading text-ink">No live deal rooms yet</div>
            <div className="text-sm text-ink-muted mt-1">
              When an interest converts, it will show up here as a lane you can move to the right.
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-5 gap-3" data-testid="velocity-board">
          {stages.map((s) => {
            const StageIcon = STAGE_ICONS[s.key] || PenLine;
            const laneCards = byStage[s.key] || [];
            return (
              <div key={s.key} className="min-w-0" data-testid={`velocity-lane-${s.key}`}>
                <div className="flex items-center justify-between mb-2 px-1">
                  <div className="flex items-center gap-1.5">
                    <StageIcon size={12} className="text-[#8A6600]" />
                    <span className="font-heading text-xs text-ink uppercase tracking-widest">{s.label}</span>
                  </div>
                  <span className="text-[10px] font-mono text-ink-muted">{totals[s.key] || 0}</span>
                </div>
                <div className="space-y-2">
                  {laneCards.length === 0 && (
                    <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted italic py-6 text-center border border-dashed border-navy/10 rounded">
                      empty
                    </div>
                  )}
                  {laneCards.map((c) => (
                    <Link
                      key={c.deal_room_id}
                      to={`/deal-rooms/${c.deal_room_id}`}
                      className="block rounded-md border border-navy/10 bg-white/40 p-3 hover:border-gold hover:shadow transition-all group"
                      data-testid={`velocity-card-${c.deal_room_id}`}
                    >
                      <div className="text-ink font-heading text-sm truncate">{c.startup_name}</div>
                      <div className="text-[11px] font-mono text-ink-muted mt-0.5 truncate">↔ {c.investor_name}</div>
                      <OwnerCue owner={c.stage_owner} hint={c.stage_hint} viewerRole={user?.role} />
                      <div className="mt-2 flex items-center justify-between text-[10px] font-mono">
                        <span className="text-ink-muted flex items-center gap-1">
                          <Clock size={10} /> {c.days_in_current_stage}d in stage
                        </span>
                        <span className="text-ink-faint group-hover:text-gold flex items-center gap-1">
                          Open <ArrowRight size={10} />
                        </span>
                      </div>
                      {/* Per-stage micro strip */}
                      <div className="mt-2 flex items-center gap-1">
                        {stages.filter(st => st.key !== "closed").map((st) => {
                          const per = c.per_stage?.[st.key];
                          const done = per?.status === "signed";
                          const active = st.key === c.current_stage;
                          return (
                            <span
                              key={st.key}
                              className={`h-1.5 flex-1 rounded ${
                                done ? "bg-emerald-500"
                                : active ? "bg-gold"
                                : "bg-navy/15"
                              }`}
                              title={`${st.label}: ${STATUS_LABEL[per?.status || "not_started"]}`}
                            />
                          );
                        })}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardShell>
  );
}

// ---------------------------------------------------------------------------
// InsightsRibbon — one-line sector comparison strip at the top of the board.
// Renders only when we have at least one signed comparison to make; otherwise
// stays out of the way so an empty state doesn't clutter the view.
// ---------------------------------------------------------------------------
function InsightsRibbon({ insights, viewerRole }) {
  if (!insights || !Array.isArray(insights.stages)) return null;
  const items = insights.stages.filter(
    (s) => s.viewer_median != null && s.sector_median != null
  );
  const sector = insights.viewer_sector;
  if (items.length === 0) {
    // No signed data yet — show a subtle onboarding hint instead of blank space.
    return (
      <div
        className="rounded-md border border-gold/30 bg-gold/[0.04] px-4 py-3 mb-4 text-xs text-ink-soft flex items-center gap-2"
        data-testid="velocity-insights-empty"
      >
        <span className="text-[#8A6600] font-heading text-[11px] uppercase tracking-widest">Velocity insights</span>
        <span>
          Once your first deal completes a stage, we&apos;ll benchmark your pace against the {sector || "sector"} median right here.
        </span>
      </div>
    );
  }
  return (
    <div
      className="rounded-md border border-gold/30 bg-gradient-to-r from-gold/[0.06] via-gold/[0.03] to-transparent px-4 py-3 mb-4"
      data-testid="velocity-insights-ribbon"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[#8A6600] font-heading text-[11px] uppercase tracking-widest">
          {sector ? `${sector} sector · ` : ""}Velocity vs peers
        </span>
        <div className="flex items-center gap-3 flex-wrap">
          {items.map((s) => {
            const diff = (s.sector_median ?? 0) - (s.viewer_median ?? 0);
            const faster = diff > 0;
            const dir = faster ? "faster" : diff < 0 ? "slower" : "on par";
            const magnitude = Math.abs(Math.round(diff));
            return (
              <span
                key={s.key}
                className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded ${
                  faster
                    ? "bg-emerald-500/10 text-emerald-800"
                    : diff < 0
                      ? "bg-rose-500/10 text-rose-800"
                      : "bg-navy/5 text-ink-soft"
                }`}
                data-testid={`velocity-insight-${s.key}`}
                title={`Your median ${s.label}: ${Math.round(s.viewer_median)}d · Sector median: ${Math.round(s.sector_median)}d`}
              >
                <span className="font-heading">{s.label}:</span>
                {diff === 0 ? (
                  <>on par with the {sector || "sector"} median ({Math.round(s.sector_median)}d)</>
                ) : viewerRole === "startup" ? (
                  <>you&apos;re {magnitude}d {dir}{sector ? ` than the ${sector} median` : ""}</>
                ) : (
                  <>{magnitude}d {dir} than sector median</>
                )}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// SectorLeaderboard — a "fastest deal rooms by sector" showcase block.
// Visible to both investors and startups to advertise our platform velocity.
// Sectors are ranked by median days-to-close (fastest wins). Sectors with no
// closed deals yet still appear ranked by active count so busy corners of the
// platform get airtime.
// ---------------------------------------------------------------------------
function SectorLeaderboard({ leaderboard }) {
  if (!leaderboard || !Array.isArray(leaderboard.sectors) || leaderboard.sectors.length === 0) {
    return null;
  }
  const sectors = leaderboard.sectors;
  const rankedSectors = sectors.filter((s) => s.median_close_days != null);
  const upcomingSectors = sectors.filter((s) => s.median_close_days == null);

  return (
    <div
      className="rounded-md border border-navy/10 bg-white/50 mb-4 overflow-hidden"
      data-testid="velocity-leaderboard"
    >
      <div className="px-4 py-3 border-b border-navy/10 bg-gradient-to-r from-gold/[0.08] via-gold/[0.03] to-transparent flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Trophy size={14} className="text-[#8A6600]" />
          <span className="font-heading text-sm text-ink">Sector Velocity Leaderboard</span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
            fastest deal rooms by sector
          </span>
        </div>
        <span className="text-[10px] font-mono text-ink-faint">
          {leaderboard.total_sectors} sector{leaderboard.total_sectors === 1 ? "" : "s"} tracked
        </span>
      </div>
      {rankedSectors.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-x divide-navy/10 border-b border-navy/10">
          {rankedSectors.slice(0, 4).map((s, i) => {
            const rankColor =
              i === 0 ? "text-[#8A6600] border-gold" :
              i === 1 ? "text-[#4B5563] border-navy/30" :
              i === 2 ? "text-[#7C4A21] border-[#7C4A21]/30" :
              "text-ink-muted border-navy/20";
            return (
              <div
                key={s.sector}
                className="p-3 flex flex-col gap-1 min-w-0"
                data-testid={`leaderboard-sector-${s.sector.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-heading uppercase tracking-widest px-1.5 py-0.5 rounded border ${rankColor}`}>
                    #{i + 1}
                  </span>
                  <span className="font-heading text-sm text-ink truncate">{s.sector}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="font-mono text-2xl text-ink">{Math.round(s.median_close_days)}</span>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                    days to close · median
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[10px] font-mono text-ink-muted">
                  <span>{s.closed_count} closed</span>
                  <span>·</span>
                  <span>{s.active_count} active</span>
                </div>
                {s.fastest && (
                  <div className="text-[10px] font-mono text-ink-soft flex items-center gap-1 mt-1 truncate">
                    <Zap size={9} className="text-[#8A6600] flex-shrink-0" />
                    <span className="truncate">Top: {s.fastest.startup_name} · {s.fastest.days}d</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {upcomingSectors.length > 0 && (
        <div className="px-4 py-2 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-heading uppercase tracking-widest text-ink-muted">
            Sectors heating up
          </span>
          {upcomingSectors.slice(0, 6).map((s) => (
            <span
              key={s.sector}
              className="text-[11px] px-2 py-0.5 rounded-full bg-navy/5 text-ink-soft font-mono"
              data-testid={`leaderboard-upcoming-${s.sector.toLowerCase().replace(/\s+/g, "-")}`}
              title={`${s.active_count} active deal room${s.active_count === 1 ? "" : "s"} — no closes yet`}
            >
              {s.sector} · {s.active_count}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
